import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-2xl"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您要查看的文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-block px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article.slug, article.category);
  const category = categoryMap[article.category];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.haijiao.cn/' },
      {
        '@type': 'ListItem',
        position: 2,
        name: category?.name || '分类',
        item: `https://www.haijiao.cn/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `https://www.haijiao.cn/article/${article.slug}`,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '海角社区',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.haijiao.cn/article/${article.slug}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-3">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  {category?.name || '分类'}
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium truncate max-w-[200px]">
                  {article.title}
                </span>
              </nav>
            </div>
          </div>

          <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {category && (
                  <Link
                    to={`/category/${article.category}`}
                    className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                )}
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                    <p className="text-xs text-foreground-500">{article.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 ml-auto text-xs text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {article.readCount.toLocaleString()}
                  </span>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i> {article.commentCount}
                  </span>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-heart-line"></i> {article.likes.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  title={`${article.title} - 海角社区`}
                  className="w-full h-[300px] md:h-[420px] object-cover"
                />
              </div>
            </header>

            {/* Article Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                [&_p]:mb-5 [&_p]:leading-7
                [&_strong]:text-foreground-900 [&_strong]:font-semibold
                prose-headings:text-foreground-900 prose-headings:font-heading"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Share */}
            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-600">分享文章：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="bg-background-100 py-12 md:py-16">
              <div className="max-w-4xl mx-auto px-4 md:px-6">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-36 overflow-hidden">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          title={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3 md:p-4">
                        <h3 className="text-xs md:text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {rel.title}
                        </h3>
                        <div className="flex items-center gap-1.5 mt-2">
                          <img
                            src={rel.authorAvatar}
                            alt={rel.author}
                            className="w-4 h-4 rounded-full object-cover"
                          />
                          <span className="text-xs text-foreground-500">{rel.author}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}